/* global SWDLocalise */
import { getClosest } from '../../utils';
import { lookupCompany } from './companies-house';
import { fillSearchSelectValue } from './search-select';
import { syncSelectFieldTargets } from './select-targets';

const STORAGE_KEY = 'swd_form_dummy_data_enabled';

// Main (applicant) company — flat `company_number` field.
const MAIN_COMPANY_NUMBER = '11118708'; // NEXT GROUP PLC

// Audit companies — repeatable `audit_company_number`, indexed by batch.
const AUDIT_COMPANY_NUMBERS = [
    '11118708', // 1st audit company: NEXT GROUP PLC
    '00035161', // 2nd audit company: NEXT HOLDINGS LIMITED
];

const auditCompanyNumber = (batchIndex) => AUDIT_COMPANY_NUMBERS[batchIndex]
    || AUDIT_COMPANY_NUMBERS[AUDIT_COMPANY_NUMBERS.length - 1];

// Names/addresses are auto-filled from the Companies House lookup, so these are
// only fallbacks used before/without a lookup.
const SAMPLE_COMPANIES = [
    'NEXT GROUP PLC',
    'NEXT HOLDINGS LIMITED',
];

// Primary-contact email — this is what the Xero contact/invoice is emailed to.
const CONTACT_EMAIL = 'samueljholguin@gmail.com';

const DUMMY_BY_KEY = {
    company_number: () => MAIN_COMPANY_NUMBER,
    audit_company_number: (batchIndex) => auditCompanyNumber(batchIndex),
    company_name: (batchIndex) => SAMPLE_COMPANIES[batchIndex % SAMPLE_COMPANIES.length],
    company_email: (batchIndex) => `company${batchIndex + 1}@example.com`,
    company_phone: () => '02079460000',
    company_address_line_1: () => '10 Test Street',
    company_address_line_2: () => 'Suite 1',
    company_address_city: () => 'London',
    company_address_region: () => 'Greater London',
    company_address_postcode: () => 'SW1A 1AA',
    company_billing_address: () => 'Yes',
    billing_address_line_1: () => '20 Billing Road',
    billing_address_line_2: () => '',
    billing_address_city: () => 'London',
    billing_address_region: () => 'Greater London',
    billing_address_postcode: () => 'EC1A 1BB',
    billing_email: () => CONTACT_EMAIL,
    // Per-audit billing email (shown when audit_billing = "no"). This is what
    // the Xero invoice is emailed to when a separate billing address is used.
    audit_billing_address_email: () => CONTACT_EMAIL,
    mpc_first_name: () => 'Jane',
    mpc_last_name: () => 'Smith',
    mpc_email: () => CONTACT_EMAIL,
    mpc_phone: () => '07123456789',
    // Primary contact (this form uses pc_* rather than mpc_*).
    pc_first_name: () => 'Jane',
    pc_last_name: () => 'Smith',
    pc_email: () => CONTACT_EMAIL,
    pc_phone: () => '07123456789',
    apc_first_name: () => 'John',
    apc_last_name: () => 'Doe',
    apc_email: (batchIndex) => `john.doe${batchIndex + 1}@example.com`,
    apc_phone: () => '07987654321',
    audit_primary_client: () => 'Primary Client Ltd',
    audit_additional_clients: () => 'Additional Client Ltd',
    audit_address_line_1: () => '5 Audit Lane',
    audit_address_line_2: () => '',
    audit_address_city: () => 'Manchester',
    audit_address_region: () => 'Greater Manchester',
    audit_address_postcode: () => 'M1 1AE',
    audit_protocol: () => 'Standard protocol',
    renewal: () => 'no',
};

const isFeatureAvailable = () => Boolean(SWDLocalise?.formDummyDataAvailable);

const getDefaultEnabled = () => Boolean(SWDLocalise?.formDummyDataEnabled);

export const isDummyDataEnabled = () => {
    if (!isFeatureAvailable()) return false;

    const stored = localStorage.getItem(STORAGE_KEY);

    if (stored === '1') return true;
    if (stored === '0') return false;

    return getDefaultEnabled();
};

export const setDummyDataEnabled = (enabled) => {
    localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0');
};

const getFieldBaseKey = (input) => input.dataset.baseKey
    || input.dataset.key?.replace(/__\d+$/, '')
    || '';

const getBatchIndex = (input) => {
    const batch = input.closest('.js-form-field-group-batch');

    return batch ? Number(batch.dataset.batchIndex || 0) : 0;
};

const shouldSkipInput = (input) => {
    if (!input.classList.contains('js-form-input')) return true;
    if (input.name === 'hp_email') return true;
    if (input.name === 'id' || input.name === 'reference') return true;
    if (input.classList.contains('is-read-only')) return true;
    if (input.type === 'file') return true;
    if (getClosest(input, '.js-form-select-target.hidden')) return true;

    return false;
};

const resolveDummyValue = (input, batchIndex) => {
    const baseKey = getFieldBaseKey(input).toLowerCase();

    if (baseKey && DUMMY_BY_KEY[baseKey]) {
        const resolver = DUMMY_BY_KEY[baseKey];

        return typeof resolver === 'function' ? resolver(batchIndex) : resolver;
    }

    if (input.type === 'email' || baseKey.includes('email')) {
        return `test+${batchIndex + 1}@example.com`;
    }

    if (baseKey.includes('phone')) {
        return `0712345678${batchIndex}`;
    }

    if (baseKey.includes('postcode')) {
        return `SW1A ${batchIndex}AA`;
    }

    if (baseKey.includes('city')) {
        return 'London';
    }

    if (baseKey.includes('region') || baseKey.includes('county')) {
        return 'Greater London';
    }

    if (baseKey.includes('address') && baseKey.includes('line_2')) {
        return '';
    }

    if (baseKey.includes('address')) {
        return `${10 + batchIndex} Example Street`;
    }

    if (baseKey.includes('first_name')) {
        return batchIndex % 2 === 0 ? 'Jane' : 'John';
    }

    if (baseKey.includes('last_name')) {
        return batchIndex % 2 === 0 ? 'Smith' : 'Doe';
    }

    if (baseKey.includes('name')) {
        return `Sample ${input.dataset.label || 'Value'} ${batchIndex + 1}`;
    }

    if (input.type === 'textarea') {
        return `Sample text for ${input.dataset.label || baseKey || 'field'}.`;
    }

    return `Sample ${input.dataset.label || baseKey || 'value'}`;
};

const fillDobFields = (input, batchIndex) => {
    const dob = input.closest('.c-field-dob');

    if (!dob) return;

    const month = dob.querySelector('.is-month');
    const day = dob.querySelector('.is-day');
    const year = dob.querySelector('.is-year');

    if (month) month.value = '01';
    if (day) day.value = String(10 + batchIndex);
    if (year) year.value = '1990';
};

const fillRadioField = (input) => {
    const radio = input.parentNode?.querySelector('.js-form-radio');

    if (!radio) return;

    radio.checked = true;
    input.value = radio.value;
    radio.dispatchEvent(new Event('click', { bubbles: true }));
};

const fillCheckboxField = (input) => {
    if (input.type !== 'checkbox') return;

    input.checked = true;
    input.dispatchEvent(new Event('input', { bubbles: true }));
};

const fillSelectField = (select, form) => {
    const options = [...select.options].filter((option) => option.value);

    if (!options.length) return;

    let selectedOption = options[0];

    if (select.classList.contains('js-form-select')) {
        const matchKey = getFieldBaseKey(select);
        const scope = select.closest('.js-form-field-group-batch') || form;
        const targets = scope.querySelectorAll('.js-form-select-target');

        const revealingOption = options.find((option) => [...targets].some(
            (target) => target.dataset.key === matchKey && target.dataset.value === option.value,
        ));

        if (revealingOption) {
            selectedOption = revealingOption;
        }
    }

    select.value = selectedOption.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
};

const fillSearchSelectField = (field, batchIndex) => {
    const value = resolveDummyValue(
        field.querySelector('.js-form-search-select-value'),
        batchIndex,
    ) || SAMPLE_COMPANIES[batchIndex % SAMPLE_COMPANIES.length];

    fillSearchSelectValue(field, value);
};

const fillInput = (input, form) => {
    if (shouldSkipInput(input)) return;

    const batchIndex = getBatchIndex(input);

    if (input.closest('.js-form-search-select')) {
        return;
    }

    if (input.classList.contains('js-form-radio-input')) {
        fillRadioField(input);
        return;
    }

    if (input.classList.contains('js-form-checkbox-input')) {
        const checkbox = input.parentNode?.querySelector('.js-form-checkbox[type=checkbox]');

        if (checkbox) {
            checkbox.checked = true;
        }

        input.value = checkbox?.value || 'yes';
        return;
    }

    if (input.closest('.c-field-dob')) {
        fillDobFields(input, batchIndex);
        return;
    }

    if (input.type === 'checkbox') {
        fillCheckboxField(input);
        return;
    }

    if (input.type === 'select-one') {
        fillSelectField(input, form);
        return;
    }

    if (input.type === 'hidden' && input.closest('.js-form-search-select')) {
        return;
    }

    if (input.classList.contains('js-form-company-number')) {
        const baseKey = getFieldBaseKey(input).toLowerCase();

        input.value = baseKey === 'company_number'
            ? MAIN_COMPANY_NUMBER
            : auditCompanyNumber(batchIndex);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        return;
    }

    input.value = resolveDummyValue(input, batchIndex);
    input.dispatchEvent(new Event('input', { bubbles: true }));
};

const triggerCompanyLookups = (form) => {
    form.querySelectorAll('.js-form-company-number').forEach((input) => {
        if (getClosest(input, '.js-form-select-target.hidden')) return;

        if (!input.value.trim()) return;

        lookupCompany(input);
    });
};

const ensureSampleBatches = (form) => {
    form.querySelectorAll('.js-form-field-group').forEach((group) => {
        const batches = group.querySelectorAll('.js-form-field-group-batch');
        const addBtn = group.querySelector('.js-form-field-group-add');

        // The group now starts empty (0 additional companies); add one so the
        // test data exercises a main + one additional company.
        if (batches.length === 0 && addBtn && !addBtn.classList.contains('hidden')) {
            addBtn.click();
        }
    });
};

export const fillFormDummyData = (form) => {
    if (!isFeatureAvailable()) return;

    ensureSampleBatches(form);

    requestAnimationFrame(() => {
        form.querySelectorAll('.js-form-select').forEach((select) => {
            if (getClosest(select, '.js-form-select-target.hidden')) return;

            fillSelectField(select, form);
        });

        syncSelectFieldTargets(form);

        form.querySelectorAll('.js-form-input').forEach((input) => {
            fillInput(input, form);
        });

        form.querySelectorAll('.js-form-search-select').forEach((field) => {
            if (getClosest(field, '.js-form-select-target.hidden')) return;

            const hiddenInput = field.querySelector('.js-form-search-select-value');
            const batchIndex = hiddenInput ? getBatchIndex(hiddenInput) : 0;

            fillSearchSelectField(field, batchIndex);
        });

        const primaryEmail = form.querySelector('.js-form-email');
        const confirmEmail = form.querySelector('.js-form-email-confirm');

        if (primaryEmail && confirmEmail) {
            confirmEmail.value = primaryEmail.value;
        }

        const consent = form.querySelector('.js-form-consent');

        if (consent) {
            consent.checked = true;
        }

        triggerCompanyLookups(form);
    });
};

const createToolbar = (form) => {
    const toolbar = document.createElement('div');

    toolbar.className = 'js-form-dummy-data-toolbar flex flex-wrap items-center gap-3 p-3 mb-4 border border-dashed border-black/25 rounded-lg bg-grey/30 text-xs';
    toolbar.innerHTML = `
        <span class="font-medium text-black/70">Form test data</span>
        <label class="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" class="js-form-dummy-data-toggle" />
            <span>Auto-fill on load</span>
        </label>
        <button
            type="button"
            class="py-1.5 px-3 border border-black rounded-full bg-black text-white font-medium js-form-dummy-data-fill">
            Fill with test data
        </button>
    `;

    const toggle = toolbar.querySelector('.js-form-dummy-data-toggle');
    const fillButton = toolbar.querySelector('.js-form-dummy-data-fill');

    toggle.checked = isDummyDataEnabled();

    toggle.addEventListener('change', (event) => {
        setDummyDataEnabled(event.target.checked);

        if (event.target.checked) {
            fillFormDummyData(form);
        }
    });

    fillButton.addEventListener('click', () => {
        fillFormDummyData(form);
    });

    return toolbar;
};

export const initFormDummyData = (form) => {
    if (!isFeatureAvailable()) return;

    const formInner = form.querySelector('.js-form-inner');

    if (!formInner || form.querySelector('.js-form-dummy-data-toolbar')) return;

    formInner.prepend(createToolbar(form));

    if (isDummyDataEnabled()) {
        requestAnimationFrame(() => fillFormDummyData(form));
    }
};
